import { useState } from 'react';

export default function CTASection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input,select,textarea').forEach(f => {
      if (f.name) data.append(f.name, f.value);
    });
    try {
      await fetch('https://readdy.ai/api/form/d77ca4sggtbbtt19ri30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
    } catch { /* silent */ }
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <section id="contact" className="relative py-28 md:py-36 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/1178c454fec18ca2a2483fea8048e275.jpg"
          alt="Event production venue"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[#080808]/92"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_80%_at_50%_0%,_#D4AF3706_0%,_transparent_70%)]"></div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Headline */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-10 h-px bg-[#D4AF37]"></div>
            <span className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase font-semibold">Get Started</span>
            <div className="w-10 h-px bg-[#D4AF37]"></div>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Let's Make Your Event<br />
            <em className="text-[#D4AF37] not-italic">One They'll Never Forget</em>
          </h2>
          <p className="text-gray-400 text-lg max-w-lg mx-auto mb-10">
            Book Seattle's premier DJ &amp; entertainment company — and give your guests an experience worth talking about.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#contact"
              onClick={(e) => e.preventDefault()}
              className="bg-[#D4AF37] hover:bg-[#c49d2a] text-black font-bold px-10 py-4 rounded-full text-sm tracking-widest uppercase transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </a>
            <a
              href="tel:2069319361"
              className="inline-flex items-center gap-3 border border-white/25 hover:border-[#D4AF37] text-white hover:text-[#D4AF37] font-semibold px-10 py-4 rounded-full text-sm tracking-widest uppercase transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-sm"></i>
              </div>
              (206) 931-9361
            </a>
          </div>
        </div>

        {/* Form */}
        {submitted ? (
          <div className="max-w-2xl mx-auto bg-[#111111] border border-[#D4AF37]/40 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mx-auto mb-5">
              <i className="ri-checkbox-circle-line text-[#D4AF37] text-3xl"></i>
            </div>
            <h3 className="text-white font-bold text-2xl mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Message Received!</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              We'll be in touch within 24 hours. You can also reach us directly at{' '}
              <a href="tel:2069319361" className="text-[#D4AF37] hover:underline">(206) 931-9361</a> or{' '}
              <a href="mailto:info@bugsieproductions.com" className="text-[#D4AF37] hover:underline">info@bugsieproductions.com</a>.
            </p>
          </div>
        ) : (
          <form
            data-readdy-form
            id="bugsie-quote-form"
            onSubmit={handleSubmit}
            className="max-w-4xl mx-auto bg-[#0F0F0F]/95 border border-white/8 rounded-2xl p-8 md:p-10"
          >
            <h3 className="text-white font-bold text-xl mb-7 tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
              Request a Free Quote
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Full Name *</label>
                <input name="name" required type="text" placeholder="Your full name"
                  className="w-full bg-[#080808] border border-white/8 focus:border-[#D4AF37]/50 text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-300 placeholder-gray-700" />
              </div>
              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Phone *</label>
                <input name="phone" required type="tel" placeholder="Your phone number"
                  className="w-full bg-[#080808] border border-white/8 focus:border-[#D4AF37]/50 text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-300 placeholder-gray-700" />
              </div>
              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Email *</label>
                <input name="email" required type="email" placeholder="your@email.com"
                  className="w-full bg-[#080808] border border-white/8 focus:border-[#D4AF37]/50 text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-300 placeholder-gray-700" />
              </div>
              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Event Type *</label>
                <select name="event_type" required
                  className="w-full bg-[#080808] border border-white/8 focus:border-[#D4AF37]/50 text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-300 cursor-pointer">
                  <option value="">Select event type</option>
                  <option>Wedding</option>
                  <option>Corporate Event</option>
                  <option>School Dance / Prom</option>
                  <option>Private Party</option>
                  <option>Photo Booth Rental</option>
                  <option>Karaoke Event</option>
                  <option>Game Show Experience</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Event Date</label>
                <input name="event_date" type="date"
                  className="w-full bg-[#080808] border border-white/8 focus:border-[#D4AF37]/50 text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-300" />
              </div>
              <div>
                <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Event Location</label>
                <input name="event_location" type="text" placeholder="City or venue name"
                  className="w-full bg-[#080808] border border-white/8 focus:border-[#D4AF37]/50 text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-300 placeholder-gray-700" />
              </div>
            </div>
            <div className="mb-7">
              <label className="block text-gray-500 text-xs tracking-widest uppercase mb-2">Message</label>
              <textarea name="message" rows={4} maxLength={500}
                placeholder="Tell us about your event, expected guest count, and any specific requirements..."
                className="w-full bg-[#080808] border border-white/8 focus:border-[#D4AF37]/50 text-white text-sm px-4 py-3 rounded-xl outline-none transition-colors duration-300 placeholder-gray-700 resize-none">
              </textarea>
            </div>
            <button type="submit" disabled={submitting}
              className="w-full bg-[#D4AF37] hover:bg-[#c49d2a] disabled:opacity-60 text-black font-bold py-4 rounded-full text-sm tracking-widest uppercase transition-all duration-300 hover:scale-[1.01] cursor-pointer whitespace-nowrap flex items-center justify-center gap-3">
              {submitting ? 'Sending...' : (
                <>
                  Send My Quote Request
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-send-plane-line text-sm"></i>
                  </div>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
